import { isFeatureEnabled } from '@luna/gatekeeper';
import { logger } from '@luna/logger';
import type { CustomFetchOptions } from '@luna/api';
import { request } from '@luna/api';
import { getEnv } from '@luna/env';

import type { TrackingEvent } from '../model/TrackingEvent';
import type { Tracker } from '../model/Tracker';
import { EventQueue } from './EventQueue';
import { skipTracking } from './helpers/skipTracking';

export type BeanCounterEvent = {
    type: string | 'page';
    name?: string;
    source?: string;
    data?: Record<string, unknown> & {
        timestamp: number;
        sessionId: string;
        url?: string;
    };
};

type IsJuniorHandler = () => Promise<boolean>;

export interface BeanCounterConfig {
    isJuniorHandler?: IsJuniorHandler;
}

export class BeanCounter implements Tracker {
    static queue = new EventQueue();

    private config: BeanCounterConfig;

    constructor(config: BeanCounterConfig = {}) {
        this.config = config;
    }

    private async hasJuniorClearance(): Promise<boolean> {
        if (!this.config.isJuniorHandler) {
            logger.warn(
                '[BeanCounter]: Junior check handler is not configured, therefore considering profile as junior.',
            );
            return false;
        }

        try {
            const isJunior = await this.config.isJuniorHandler();

            if (!isJunior) {
                return true;
            }

            logger.debug('[BeanCounter]: Junior profile detected.');

            return isFeatureEnabled((features) => features.junior.beancounterTrackingEnabled);
        } catch {
            logger.warn(
                '[BeanCounter]: Junior check failed, therefore considering profile as junior.',
            );
            return false;
        }
    }

    async track(event: TrackingEvent<any>, isInstant = false): Promise<void> {
        if (!isFeatureEnabled((f) => f.beanCounterTracking)) {
            return;
        }
        if (BeanCounter.queue.isStopped) {
            return;
        }

        if (!(await this.hasJuniorClearance())) {
            logger.debug('[BeanCounter]: Tracking disabled due to junior profile.');
            return;
        }

        // automatic "pageChanged" tracking events = page; otherwise = event
        const eventType = event.category === 'page' ? event.category : 'event';

        const beanCounterEvent: BeanCounterEvent = {
            type: eventType,
            name: event.name,
            source: event.source,
            data: {
                ...event.payload,
                timestamp: event.timestamp,
                sessionId: event.sessionId,
            },
        };
        if (BeanCounter.hasBeanCounterUrl(beanCounterEvent)) {
            return;
        }

        if (skipTracking()) {
            logger.info('BeanCounter#track', beanCounterEvent);
            return;
        }

        if (isInstant) {
            await BeanCounter.sendEvent(beanCounterEvent).then(() => this.flushQueue());
            return;
        }

        await BeanCounter.queue.track(() => BeanCounter.sendEvent(beanCounterEvent));
    }

    reset() {
        BeanCounter.queue.reset();
    }

    stop() {
        BeanCounter.queue.stop();
    }

    private static hasBeanCounterUrl(event: BeanCounterEvent): boolean {
        return event?.data?.url?.includes('events/async') ?? false;
    }

    /**
     * Empties queue by sending events 1-by-1. Queue will not be emptied if there is a flushing already in progress.
     */
    async flushQueue(): Promise<void> {
        return BeanCounter.queue.flushQueue();
    }

    private static async sendEvent(event: BeanCounterEvent): Promise<void> {
        const config = getEnv();

        const options: CustomFetchOptions = {
            headers: {
                'Content-Type': 'application/json',
            },
            priority: 'low',
        };

        if (config.webAPIClientId) {
            options.headers = {
                'WEB-API-key': config.webAPIClientId,
            };
        }

        await request.post<unknown>(`${config.beanCounterUrl}events/async`, event, options);
    }

    updateConfig(config: Partial<BeanCounterConfig>): void {
        this.config = {
            ...this.config,
            ...config,
        };
    }
}
